/**
 * Enquiries from the website's contact form.
 *
 * Laid out like the mail client everyone already knows: a list on the left, the open
 * message on the right; on a phone, the list, then the message full-screen with a way
 * back. The point of opening this page is to read what somebody wrote and answer them,
 * so Reply and Call are the two biggest things on the message — Reply opens the
 * office's own mail program addressed and titled, rather than a reply box here that
 * would send from a no-reply address the customer cannot answer.
 *
 * Opening a message marks it read, by whom and when: "did anyone see the one from
 * Tuesday" is the question the office actually asks about an inbox.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge, Card, EmptyState, ErrorNotice, PageHeader, Spinner } from '@/components/ui';
import { useDir, useI18n } from '@/i18n';
import { api, type ContactMessage } from '@/lib/api';
import { dateTime } from '@/lib/format';
import { usePermissions } from '@/features/access/usePermissions';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '')).toUpperCase();
}

export function MessagesPage() {
  const { t } = useI18n();
  const { back } = useDir();
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const canAct = can('messages', 'write');

  const [archived, setArchived] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const inbox = useQuery({
    queryKey: ['messages', archived],
    queryFn: () => api.messages(archived),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['messages'] });
  const markRead = useMutation({ mutationFn: api.readMessage, onSuccess: invalidate });
  const archive = useMutation({
    mutationFn: api.archiveMessage,
    onSuccess: async () => {
      setOpenId(null);
      await invalidate();
    },
  });
  const restore = useMutation({
    mutationFn: api.restoreMessage,
    onSuccess: async () => {
      setOpenId(null);
      await invalidate();
    },
  });

  const list = useMemo(() => {
    const all = inbox.data?.messages ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((m) =>
      [m.name, m.company, m.email, m.phone, m.subject, m.message].some((v) => v?.toLowerCase().includes(q)),
    );
  }, [inbox.data, query]);

  const open = list.find((m) => m.id === openId) ?? null;

  const select = (m: ContactMessage) => {
    setOpenId(m.id);
    // Viewing is reading — but only someone who may act on the inbox changes its state.
    if (m.status === 'new' && canAct) markRead.mutate(m.id);
  };

  const unread = inbox.data?.unread ?? 0;

  return (
    <>
      <PageHeader title={t('messages.title')} subtitle={t('messages.subtitle')} />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex" role="tablist">
          {([false, true] as const).map((isArchived, i) => (
            <button
              key={String(isArchived)}
              type="button"
              role="tab"
              aria-selected={archived === isArchived}
              onClick={() => {
                setArchived(isArchived);
                setOpenId(null);
              }}
              className={[
                'h-9 px-4 inline-flex items-center gap-2 border text-sm font-medium transition-colors',
                i === 0 ? 'rounded-s' : '-ms-px rounded-e',
                archived === isArchived
                  ? 'bg-[var(--color-primary-soft)] text-primary border-[var(--color-border-strong)] font-semibold relative z-10'
                  : 'bg-surface text-muted border-border hover:text-text hover:bg-surface-alt',
              ].join(' ')}
            >
              {t(isArchived ? 'messages.archived' : 'messages.inbox')}
              {!isArchived && unread > 0 ? <Badge tone="primary">{unread}</Badge> : null}
            </button>
          ))}
        </div>

        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('messages.search')}
          aria-label={t('messages.search')}
          className="input h-9 min-w-0 flex-1 sm:max-w-xs"
        />
      </div>

      {inbox.isLoading ? (
        <Spinner />
      ) : inbox.error ? (
        <ErrorNotice error={inbox.error} />
      ) : list.length === 0 ? (
        <EmptyState
          title={t(query ? 'messages.noMatches' : archived ? 'messages.emptyArchived' : 'messages.empty')}
          message={archived || query ? undefined : t('messages.emptyHint')}
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(280px,380px)_1fr] lg:items-start">
          {/* The list. On a phone it steps aside while a message is open. */}
          <Card padded={false} className={open ? 'max-lg:hidden' : undefined}>
            <ul className="divide-y divide-border">
              {list.map((m) => {
                const selected = m.id === openId;
                const isNew = m.status === 'new';
                return (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => select(m)}
                      aria-current={selected}
                      className={[
                        'flex w-full gap-3 px-3.5 py-3 text-start transition-colors',
                        selected ? 'bg-[var(--color-primary-soft)]' : 'hover:bg-surface-alt',
                      ].join(' ')}
                    >
                      <span
                        className={[
                          'mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                          isNew ? 'bg-primary text-white' : 'bg-surface-alt text-muted',
                        ].join(' ')}
                        aria-hidden="true"
                      >
                        {initials(m.name)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline justify-between gap-2">
                          <span className={`truncate text-sm ${isNew ? 'font-bold' : 'font-medium'}`}>{m.name}</span>
                          <span className="shrink-0 text-[11px] tabular text-subtle">{dateTime(m.createdAt)}</span>
                        </span>
                        <span className={`block truncate text-xs ${isNew ? 'text-text font-semibold' : 'text-muted'}`}>
                          {m.subject ?? t('messages.noSubject')}
                        </span>
                        <span className="block truncate text-xs text-subtle">{m.message}</span>
                      </span>
                      {isNew ? (
                        <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" aria-label={t('messages.new')} />
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>

          {/* The reading pane. */}
          {open ? (
            <Card>
              <button
                type="button"
                onClick={() => setOpenId(null)}
                className="mb-3 text-sm font-semibold text-primary lg:hidden"
              >
                {back} {t('messages.back')}
              </button>

              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--color-primary-soft)] text-sm font-bold text-primary">
                    {initials(open.name)}
                  </span>
                  <div className="min-w-0">
                    <h2 className="truncate text-lg font-bold">{open.name}</h2>
                    {open.company ? <div className="truncate text-sm text-muted">{open.company}</div> : null}
                  </div>
                </div>
                <div className="text-xs tabular text-subtle">{dateTime(open.createdAt)}</div>
              </div>

              <div className="mt-4 rounded-lg bg-surface-alt px-4 py-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-subtle">
                  {open.subject ?? t('messages.noSubject')}
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed">{open.message}</p>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <a
                  className="btn-primary"
                  href={`mailto:${open.email}?subject=${encodeURIComponent(`Re: ${open.subject ?? 'HH Electrical Systems'}`)}`}
                >
                  {t('messages.reply')}
                </a>
                {open.phone ? (
                  <a className="btn-secondary" href={`tel:${open.phone.replace(/\s+/g, '')}`}>
                    {t('messages.call')} {open.phone}
                  </a>
                ) : null}
                {canAct ? (
                  open.status === 'archived' ? (
                    <button type="button" className="btn-secondary" disabled={restore.isPending} onClick={() => restore.mutate(open.id)}>
                      {t('messages.restore')}
                    </button>
                  ) : (
                    <button type="button" className="btn-secondary" disabled={archive.isPending} onClick={() => archive.mutate(open.id)}>
                      {t('messages.archive')}
                    </button>
                  )
                ) : null}
              </div>

              <dl className="mt-5 grid gap-x-6 gap-y-1.5 border-t border-border pt-4 text-xs sm:grid-cols-2">
                <div className="min-w-0">
                  <dt className="text-subtle">{t('messages.email')}</dt>
                  <dd className="truncate">
                    <a className="font-medium text-primary hover:underline" href={`mailto:${open.email}`}>
                      {open.email}
                    </a>
                  </dd>
                </div>
                <div>
                  <dt className="text-subtle">{t('messages.language')}</dt>
                  <dd>{t(open.locale === 'en' ? 'messages.wroteInEnglish' : open.locale === 'ar' ? 'messages.wroteInArabic' : 'messages.wroteInItalian')}</dd>
                </div>
                {open.readByName ? (
                  <div className="sm:col-span-2">
                    <dt className="text-subtle">{t('messages.readLabel')}</dt>
                    <dd>{t('messages.readBy', { name: open.readByName, when: dateTime(open.readAt ?? open.createdAt) })}</dd>
                  </div>
                ) : null}
              </dl>
            </Card>
          ) : (
            <Card className="max-lg:hidden">
              <p className="py-16 text-center text-sm text-subtle">{t('messages.selectOne')}</p>
            </Card>
          )}
        </div>
      )}
    </>
  );
}
